// API configuration with environment variable support
// Use the same host as the frontend is accessed from
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Use the same host and port as frontend (nginx proxy handles routing)
  const protocol = window.location.protocol;
  const host = window.location.host; // includes port if any
  return `${protocol}//${host}/api`;
};

export const API_BASE_URL = getApiBaseUrl();
export const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'development';

// API endpoints
export const API_ENDPOINTS = {
  // Steering endpoints
  STEERING_HLR: `${API_BASE_URL}/v1/steering/hlr`,
  STEERING_HSS: `${API_BASE_URL}/v1/steering/hss`,
  
  // Operators endpoints
  OPERATORS: `${API_BASE_URL}/v1/operators`,
  OPERATOR_COUNTRIES: `${API_BASE_URL}/v1/operators/countries`,
  OPERATOR_BY_ID: (id: number) => `${API_BASE_URL}/v1/operators/${id}`,
  OPERATOR_GT_PREFIXES: (id: number) => `${API_BASE_URL}/v1/operators/${id}/gt-prefixes`,
  OPERATORS_BULK_TOGGLE: `${API_BASE_URL}/v1/operators/bulk-toggle`,
  
  // GT Prefixes endpoints
  // Declared on the operators router as "/gt-prefixes/{id}", so the real path
  // carries the /operators prefix. It was missing here, and edit and delete of
  // a GT prefix have been 404ing as a result.
  GT_PREFIX_BY_ID: (id: number) => `${API_BASE_URL}/v1/operators/gt-prefixes/${id}`,
  
  // Logs endpoints
  LOGS: `${API_BASE_URL}/v1/logs`,
  LOGS_EXPORT: `${API_BASE_URL}/v1/logs/export`,
  
  // Unknown networks endpoints
  UNKNOWN_NETWORKS: `${API_BASE_URL}/v1/unknown-networks`,
  UNKNOWN_NETWORK_RESOLVE: (id: number) => `${API_BASE_URL}/v1/unknown-networks/${id}/resolve`,
  UNKNOWN_NETWORK_ADD_OPERATOR: (id: number) => `${API_BASE_URL}/v1/unknown-networks/${id}/add-operator`,
  
  // Stats endpoints
  STATS_OVERVIEW: `${API_BASE_URL}/v1/stats/overview`,
  STATS_BY_COUNTRY: `${API_BASE_URL}/v1/stats/by-country`,
  STATS_BY_OPERATOR: `${API_BASE_URL}/v1/stats/by-operator`,
  STATS_TIMELINE: `${API_BASE_URL}/v1/stats/timeline`,
  STATS_TOP_UNKNOWN: `${API_BASE_URL}/v1/stats/top-unknown`,
  
  // Brands endpoints.
  // Every child route repeats the brand id, matching how the backend declares
  // them, which is what GT_PREFIX_BY_ID above failed to do.
  BRANDS: `${API_BASE_URL}/v1/brands`,
  BRAND_BY_ID: (id: number) => `${API_BASE_URL}/v1/brands/${id}`,
  BRAND_RESOLVE: `${API_BASE_URL}/v1/brands/resolve`,
  BRAND_IMSI_SERIES: (brandId: number) => `${API_BASE_URL}/v1/brands/${brandId}/imsi-series`,
  BRAND_IMSI_SERIES_BY_ID: (brandId: number, seriesId: number) =>
    `${API_BASE_URL}/v1/brands/${brandId}/imsi-series/${seriesId}`,
  BRAND_NETWORKS: (brandId: number) => `${API_BASE_URL}/v1/brands/${brandId}/networks`,
  BRAND_NETWORKS_COPY_TEMPLATE: (brandId: number) =>
    `${API_BASE_URL}/v1/brands/${brandId}/networks/copy-from-global`,

  // Auth and users
  AUTH_LOGIN: `${API_BASE_URL}/v1/auth/login`,
  AUTH_LOGOUT: `${API_BASE_URL}/v1/auth/logout`,
  AUTH_ME: `${API_BASE_URL}/v1/auth/me`,
  AUTH_CHANGE_PASSWORD: `${API_BASE_URL}/v1/auth/change-password`,
  USERS: `${API_BASE_URL}/v1/users`,
  USER_BY_ID: (id: number) => `${API_BASE_URL}/v1/users/${id}`,

  // Stats
  STATS_BY_BRAND: `${API_BASE_URL}/v1/stats/by-brand`,

  // Config endpoints
  CONFIG_TELEGRAM: `${API_BASE_URL}/v1/config/telegram`,
  CONFIG_TELEGRAM_TEST: `${API_BASE_URL}/v1/config/telegram/test`,
  
  // Health check
  HEALTH: `${API_BASE_URL}/v1/health`,
};

/** Build a query string, skipping empty values and the lone '?'.
 *
 * The existing list methods each inline their own copy of this; those are left
 * alone rather than rewritten, but new callers use this one. */
export function buildQuery(params?: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      qs.append(key, String(value));
    }
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// Request configuration
export const getRequestConfig = () => ({
  headers: {
    'Content-Type': 'application/json',
  },
  credentials: 'include' as RequestCredentials,
});

// Error handling
export class ApiError extends Error {
  constructor(public status: number, message: string, public data?: any) {
    super(message);
    this.name = 'ApiError';
  }
}

// API request wrapper with error handling
export async function apiRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    const response = await fetch(url, {
      ...getRequestConfig(),
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.detail || response.statusText,
        errorData
      );
    }

    // An empty body would make response.json() throw, and that throw is then
    // re-wrapped as a network error -- which would show up as a red failure
    // banner right after a successful delete.
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }

    return response.json();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new Error('Network error: ' + (error as Error).message);
  }
}